import { useFocusEffect, useRouter, type Router } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  Bell,
  ChevronLeft,
  ChevronRight,
  CloudUpload,
  CreditCard,
  KeyRound,
  LifeBuoy,
  LogOut,
  Mail,
  ShieldCheck,
  Pencil,
  User,
  Users,
} from 'lucide-react-native';
import { ComponentType, useCallback, useState } from 'react';
import { Linking, Modal, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { goBackOrTo } from '@/lib/nav';
import { now } from '@/db/ids';
import type { PropertyRow } from '@/db/types';
import { bumpDataRevision } from '@/lib/data-revision';
import { getActiveProperty } from '@/lib/tasks';
import { webNoOutline } from '@/lib/web-style';

/**
 * /account — the user-account surface.
 *
 * Wave 47 (launch prep): everything that ALREADY works in the app is a
 * live, tappable row — notifications, data export, help, email. Only
 * the genuinely-future capabilities (sign-in, family sharing,
 * subscription, cloud backup) keep the "Coming soon" badge, so the
 * page reads as a working dashboard with a short roadmap tail instead
 * of a wall of stubs.
 */

const SUPPORT_INBOX = 'support@smartsiteservices.com';

interface AccountRow {
  label: string;
  sub: string;
  Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  /** Tint used for the icon chip — keeps the page from being a wall of
   *  monochrome. Maps loosely to row meaning (key=auth, bell=notifs, etc). */
  bg: string;
  /** Live rows navigate; absent = Coming soon badge. */
  onPress?: (router: Router) => void;
}

const IDENTITY_ROWS: AccountRow[] = [
  // "Profile" graduated from this coming-soon list to the live editable
  // card at the top of the page (Wave 72).
  {
    label: 'Sign in / Create account',
    sub: 'One account, every device. Pre-launch: still single-device.',
    Icon: KeyRound,
    bg: Palette.honeyDeep,
  },
  {
    label: 'Family sharing',
    sub: 'Co-owners + housemates with shared access per home.',
    Icon: Users,
    bg: Palette.forestLight,
  },
];

const PLAN_ROWS: AccountRow[] = [
  {
    label: 'Subscription',
    sub: 'Free (1 home) vs Paid (5 homes). Currently everyone is on Paid pre-launch.',
    Icon: CreditCard,
    bg: Palette.clay,
  },
  {
    label: 'Cloud backup',
    sub: 'Mirror everything to your account so a lost phone is no big deal.',
    Icon: CloudUpload,
    bg: Palette.moss,
  },
];

const SAFETY_ROWS: AccountRow[] = [
  {
    label: 'Notifications',
    sub: 'Digest cadence per home, quiet hours, per-category pings.',
    Icon: Bell,
    bg: Palette.honey,
    onPress: (router) => router.push('/notifications' as never),
  },
  {
    label: 'Your data',
    sub: 'Everything lives on your device. Export or restore a full backup any time in Settings.',
    Icon: ShieldCheck,
    bg: Palette.forestGlow,
    onPress: (router) => router.push('/(tabs)/settings' as never),
  },
  {
    label: 'Privacy policy',
    sub: 'Local-first, no accounts, nothing sold — the whole policy in plain words.',
    Icon: ShieldCheck,
    bg: Palette.forest,
    onPress: (router) => router.push('/privacy' as never),
  },
];

const SUPPORT_ROWS: AccountRow[] = [
  {
    label: 'Help & feedback',
    sub: 'Questions, ideas, something broken — tell us.',
    Icon: LifeBuoy,
    bg: Palette.forestLight,
    onPress: (router) => router.push('/support' as never),
  },
  {
    label: 'Email us',
    sub: SUPPORT_INBOX,
    Icon: Mail,
    bg: Palette.honeyDeep,
    onPress: () => {
      void Linking.openURL(`mailto:${SUPPORT_INBOX}?subject=Homestead`).catch(() => {});
    },
  },
  {
    label: 'Sign out',
    sub: 'Disabled until sign-in lands.',
    Icon: LogOut,
    bg: Palette.clay,
  },
];

export default function Account() {
  const router = useRouter();
  const db = useSQLiteContext();
  const [property, setProperty] = useState<PropertyRow | null>(null);
  // Profile editor modal (Wave 72): first/last name, email, phone.
  const [editorOpen, setEditorOpen] = useState(false);
  const [eFirst, setEFirst] = useState('');
  const [eLast, setELast] = useState('');
  const [eEmail, setEEmail] = useState('');
  const [ePhone, setEPhone] = useState('');

  const load = useCallback(async () => {
    setProperty(await getActiveProperty(db));
  }, [db]);
  useFocusEffect(useCallback(() => { void load(); }, [load]));

  const openEditor = () => {
    setEFirst(property?.owner_first_name ?? '');
    setELast(property?.owner_last_name ?? '');
    setEEmail(property?.owner_email ?? '');
    setEPhone(property?.owner_phone ?? '');
    setEditorOpen(true);
  };

  const saveProfile = async () => {
    if (!property) return;
    const f = eFirst.trim() || null;
    const l = eLast.trim() || null;
    const em = eEmail.trim() || null;
    const ph = ePhone.trim() || null;
    setProperty({ ...property, owner_first_name: f, owner_last_name: l, owner_email: em, owner_phone: ph });
    setEditorOpen(false);
    await db.runAsync(
      `UPDATE property SET owner_first_name = ?, owner_last_name = ?, owner_email = ?, owner_phone = ?, updated_at = ? WHERE id = ?`,
      f, l, em, ph, now(), property.id
    );
    bumpDataRevision();
  };

  const fullName = [property?.owner_first_name, property?.owner_last_name]
    .filter(Boolean)
    .join(' ');
  const initial =
    (property?.owner_first_name ?? property?.name ?? 'H').trim().charAt(0).toUpperCase() || 'H';
  const contactLine = [property?.owner_email, property?.owner_phone].filter(Boolean).join(' · ');

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable
          onPress={() => goBackOrTo(router, '/(tabs)/settings')}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Account</Text>
        <View style={{ width: 36 }} />
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 60 }}>
        {/* Live profile card (Wave 72) — the avatar in the header lands
            here, so the first thing on the page is YOU, editable. */}
        <Pressable
          onPress={openEditor}
          disabled={!property}
          style={({ pressed }) => [styles.profileCard, pressed && { opacity: 0.92 }]}>
          <View style={[styles.profileAvatar, { backgroundColor: property?.accent_color ?? Palette.forest }]}>
            <Text style={styles.profileAvatarText}>{initial}</Text>
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={styles.profileName} numberOfLines={1}>
              {fullName || 'Add your name'}
            </Text>
            <Text style={styles.profileMeta} numberOfLines={1}>
              {contactLine || 'Email + phone — used on binder exports'}
            </Text>
          </View>
          <View style={styles.profileEditChip}>
            <Pencil size={12} color={Palette.forest} strokeWidth={2} />
            <Text style={styles.profileEditText}>Edit</Text>
          </View>
        </Pressable>

        <Section title="NOTIFICATIONS & PRIVACY" rows={SAFETY_ROWS} router={router} />
        <Section title="SUPPORT" rows={SUPPORT_ROWS} router={router} />
        <Section title="IDENTITY" rows={IDENTITY_ROWS} router={router} />
        <Section title="PLAN & BACKUP" rows={PLAN_ROWS} router={router} />

        <View style={styles.footnote}>
          <Text style={styles.footnoteText}>
            Pre-launch · Single-device · No account required
          </Text>
        </View>
      </ScrollView>

      {/* Profile editor */}
      <Modal transparent animationType="fade" visible={editorOpen} onRequestClose={() => setEditorOpen(false)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setEditorOpen(false)}>
          <Pressable style={styles.modalCard} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Your profile</Text>
            <Text style={styles.modalSub}>
              Lives on this device for now — it personalizes the greeting
              and fills in your info on binder + record exports. It becomes
              your account when sign-in lands.
            </Text>
            <View style={styles.modalRow2}>
              <TextInput
                value={eFirst}
                onChangeText={setEFirst}
                placeholder="First name"
                placeholderTextColor={Palette.inkFaint}
                autoCapitalize="words"
                style={[styles.modalInput, { flex: 1 }, webNoOutline]}
              />
              <TextInput
                value={eLast}
                onChangeText={setELast}
                placeholder="Last name"
                placeholderTextColor={Palette.inkFaint}
                autoCapitalize="words"
                style={[styles.modalInput, { flex: 1 }, webNoOutline]}
              />
            </View>
            <TextInput
              value={eEmail}
              onChangeText={setEEmail}
              placeholder="Email"
              placeholderTextColor={Palette.inkFaint}
              autoCapitalize="none"
              inputMode="email"
              style={[styles.modalInput, webNoOutline]}
            />
            <TextInput
              value={ePhone}
              onChangeText={setEPhone}
              placeholder="Phone"
              placeholderTextColor={Palette.inkFaint}
              inputMode="tel"
              style={[styles.modalInput, webNoOutline]}
            />
            <Pressable
              onPress={() => void saveProfile()}
              style={({ pressed }) => [styles.modalSave, pressed && { transform: [{ scale: 0.98 }] }]}>
              <Text style={styles.modalSaveText}>Save profile</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>
    </SafeAreaView>
  );
}

function Section({ title, rows, router }: { title: string; rows: AccountRow[]; router: Router }) {
  return (
    <View>
      <Text style={styles.sectionLabel}>{title}</Text>
      <View style={styles.card}>
        {rows.map((r, i) => {
          const live = !!r.onPress;
          const inner = (
            <>
              <View style={[styles.rowIcon, { backgroundColor: r.bg }]}>
                <r.Icon size={16} color={Palette.cream} strokeWidth={2} />
              </View>
              <View style={{ flex: 1, minWidth: 0 }}>
                <View style={styles.rowTopLine}>
                  <Text style={styles.rowLabel}>{r.label}</Text>
                  {!live ? (
                    <View style={styles.soonBadge}>
                      <Text style={styles.soonBadgeText}>Coming soon</Text>
                    </View>
                  ) : null}
                </View>
                <Text style={styles.rowSub}>{r.sub}</Text>
              </View>
              {live ? (
                <ChevronRight size={16} color={Palette.inkFaint} strokeWidth={2} />
              ) : null}
            </>
          );
          const rowStyle = [styles.row, i < rows.length - 1 && styles.rowBorder];
          return live ? (
            <Pressable
              key={r.label}
              onPress={() => r.onPress!(router)}
              style={({ pressed }) => [...rowStyle, pressed && { backgroundColor: '#EEF3EE' }]}>
              {inner}
            </Pressable>
          ) : (
            <View key={r.label} style={rowStyle}>
              {inner}
            </View>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  profileCard: {
    flexDirection: 'row', alignItems: 'center', gap: 14,
    marginHorizontal: Spacing.four, marginTop: Spacing.three,
    backgroundColor: Palette.card,
    borderWidth: 1.5, borderColor: Palette.line,
    borderRadius: Radius.xl, padding: Spacing.four,
  },
  profileAvatar: {
    width: 54, height: 54, borderRadius: 27,
    alignItems: 'center', justifyContent: 'center',
  },
  profileAvatarText: {
    fontFamily: Fonts.serifBold, fontSize: 24, color: Palette.cream,
  },
  profileName: {
    fontFamily: Fonts.serifBold, fontSize: 19, color: Palette.ink,
    letterSpacing: -0.2,
  },
  profileMeta: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
  },
  profileEditChip: {
    flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 11, paddingVertical: 7,
    borderRadius: 999, borderWidth: 1.5, borderColor: Palette.forest,
    backgroundColor: '#EEF3EE',
  },
  profileEditText: { fontFamily: Fonts.sansBold, fontSize: 12, color: Palette.forest },
  modalBackdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  modalCard: {
    backgroundColor: Palette.sand, borderRadius: 24,
    padding: 20, width: '100%', maxWidth: 400,
  },
  modalTitle: { fontFamily: Fonts.serifBold, fontSize: 19, color: Palette.ink },
  modalSub: {
    marginTop: 4, marginBottom: 12,
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.inkSoft,
    lineHeight: 18,
  },
  modalRow2: { flexDirection: 'row', gap: 8 },
  modalInput: {
    marginBottom: 8, minWidth: 0,
    borderWidth: 1.5, borderColor: Palette.line,
    backgroundColor: Palette.card, borderRadius: 12,
    paddingHorizontal: 13, paddingVertical: 11,
    fontFamily: Fonts.sansSemibold, fontSize: 14.5, color: Palette.ink,
  },
  modalSave: {
    marginTop: 6, alignItems: 'center',
    backgroundColor: Palette.forest,
    paddingVertical: 13, borderRadius: 14,
  },
  modalSaveText: { fontFamily: Fonts.sansBold, fontSize: 14.5, color: Palette.cream },

  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.two,
    paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },

  header: { paddingHorizontal: 22, paddingTop: Spacing.three },
  h1: { fontFamily: Fonts.serif, fontSize: 27, color: Palette.ink, letterSpacing: -0.3 },
  sub: {
    marginTop: 4,
    fontSize: 13,
    color: Palette.inkSoft,
    fontFamily: Fonts.sansMedium,
    lineHeight: 19,
  },

  sectionLabel: {
    marginTop: Spacing.five,
    marginHorizontal: 22,
    marginBottom: 8,
    fontFamily: Fonts.sansBold,
    fontSize: 12,
    color: Palette.inkFaint,
    letterSpacing: 0.8,
  },
  card: {
    marginHorizontal: 18,
    backgroundColor: Palette.card,
    borderRadius: Radius.xl,
    borderWidth: 1,
    borderColor: Palette.line,
    overflow: 'hidden',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  rowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: Palette.line,
  },
  rowIcon: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowTopLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  rowLabel: {
    flex: 1,
    fontFamily: Fonts.sansBold,
    fontSize: 14,
    color: Palette.ink,
  },
  rowSub: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
    lineHeight: 17,
  },
  soonBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: 'rgba(225,163,61,0.18)',
    borderWidth: 1,
    borderColor: 'rgba(194,132,42,0.35)',
  },
  soonBadgeText: {
    fontFamily: Fonts.sansBold,
    fontSize: 11,
    color: Palette.honeyDeep,
    letterSpacing: 0.4,
  },

  footnote: {
    marginTop: Spacing.five,
    marginHorizontal: 22,
    paddingTop: Spacing.three,
    borderTopWidth: 1,
    borderTopColor: Palette.line,
    alignItems: 'center',
  },
  footnoteText: {
    fontFamily: Fonts.sansSemibold,
    fontSize: 12,
    color: Palette.inkFaint,
    letterSpacing: 0.4,
  },
});
